import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/app/router/route-constants';
import { useTranslation } from '@/app/providers';
import { useAppSelector } from '@/app/store/hooks';
import { useActiveBranch } from '@/features/auth/hooks/use-active-branch';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { selectUser } from '@/features/auth/store/auth-slice';
import { getDisplayName } from '@/features/auth/utils/get-display-name';
import { Avatar } from '@/shared/components/avatar';
import { cn } from '@/shared/lib/cn';
import { SearchInput } from './search-input';

interface ProfileDropdownProps {
  variant?: 'light' | 'dark';
}

function SettingsIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  );
}

export function ProfileDropdown({ variant = 'light' }: ProfileDropdownProps) {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const { logout } = useAuth();
  const { branchCode, branches, switchBranch, isSwitchingBranch } =
    useActiveBranch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredBranches = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return branches;

    return branches.filter(
      (branch) =>
        branch.branchName.toLowerCase().includes(query) ||
        branch.branchCode.toLowerCase().includes(query),
    );
  }, [branches, search]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearch('');
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!user) return null;

  const fullName = getDisplayName(user);
  const isDark = variant === 'dark';

  const handleBranchSelect = (code: string) => {
    if (code === branchCode || isSwitchingBranch) return;
    void switchBranch(code);
    closeDropdown();
    void navigate(ROUTES.DASHBOARD);
  };
  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => {
          if (isOpen) {
            closeDropdown();
          } else {
            setIsOpen(true);
          }
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={fullName}
        className={cn(
          'flex items-center rounded-lg p-1.5 transition-colors',
          isDark ? 'hover:bg-surface-hover' : 'hover:bg-muted',
        )}
      >
        <Avatar name={fullName} src={user.avatar} size="sm" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40 first-letter:uppercase"
            onClick={closeDropdown}
            aria-hidden="true"
          />
          <div
            role="menu"
            className={cn(
              'absolute right-0 z-50 mt-2 flex w-72 max-h-[calc(100dvh-5.5rem-env(safe-area-inset-bottom,0px))] flex-col overflow-hidden rounded-xl border-0 shadow-xl animate-slide-down',
              isDark
                ? 'border-surface-border bg-surface text-white'
                : 'bg-card text-body text-border',
            )}
          >
            <div
              className={cn(
                'shrink-0 border-b px-4 py-3',
                isDark ? 'border-surface-border' : 'text-border',
              )}
            >
              {user.email && (
                <p className="truncate text-sm font-medium">{user.email}</p>
              )}
              <p
                className={cn(
                  'mt-0.5 text-xs',
                  isDark ? 'text-surface-muted' : 'text-secondary',
                )}
              >
                {t('header.userCode')}: {user.userCode}
              </p>
            </div>

            {branches.length > 0 && (
              <div
                className={cn(
                  'flex min-h-0 flex-1 flex-col border-b px-4 py-3',
                  isDark ? 'border-surface-border' : 'text-border',
                )}
              >
                <p
                  className={cn(
                    'mb-2 shrink-0 text-xs font-semibold uppercase tracking-wide',
                    isDark ? 'text-surface-muted' : 'text-secondary',
                  )}
                >
                  {t('header.branchList')}
                </p>
                <div className="shrink-0">
                  <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder={t('header.searchBranches')}
                  />
                </div>
                {filteredBranches.length === 0 ? (
                  <p
                    className={cn(
                      'shrink-0 px-2 py-2 text-xs',
                      isDark ? 'text-surface-muted' : 'text-secondary',
                    )}
                  >
                    {t('header.noBranchesFound')}
                  </p>
                ) : (
                  <ul className="mt-1 min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain">
                    {filteredBranches.map((branch) => {
                      const isSelected = branch.branchCode === branchCode;
                      return (
                        <li key={branch.branchCode}>
                          <button
                            type="button"
                            disabled={isSwitchingBranch}
                            onClick={() => {
                              handleBranchSelect(branch.branchCode);
                            }}
                            className={cn(
                              'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm transition-colors disabled:opacity-50',
                              isSelected
                                ? isDark
                                  ? 'bg-primary/15 text-primary'
                                  : 'bg-primary/10 text-primary'
                                : isDark
                                  ? 'hover:bg-surface-hover'
                                  : 'hover:bg-muted',
                            )}
                          >
                            <span
                              className={cn(
                                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                                isSelected
                                  ? 'border-primary'
                                  : isDark
                                    ? 'border-surface-muted'
                                    : 'text-border',
                              )}
                            >
                              {isSelected && (
                                <span className="h-2 w-2 rounded-full bg-primary" />
                              )}
                            </span>
                            <span className="truncate">
                              {branch.branchName}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {isSwitchingBranch && (
                  <p className="mt-2 shrink-0 text-xs text-primary">
                    {t('header.switchingBranch')}
                  </p>
                )}
              </div>
            )}

            <div className="shrink-0 pb-[max(0px,env(safe-area-inset-bottom,0px))]">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeDropdown();
                  void navigate(ROUTES.USER_SETTINGS);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 border-b px-4 py-3 text-left text-sm transition-colors',
                  isDark
                    ? 'border-surface-border hover:bg-surface-hover'
                    : 'text-border hover:bg-muted',
                )}
              >
                <SettingsIcon />
                {t('nav.settings')}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeDropdown();
                  logout();
                }}
                className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm text-danger transition-colors hover:bg-danger/10"
              >
                <LogoutIcon />
                {t('header.signOut')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
